import React, { useEffect, useMemo, useState } from 'react';
import LoadingFallback from 'containers/antifraud/loading-fallback';
import { Title, BizonSidebar } from 'components/commons';
import BizosButton from 'components/bizos/bizos-button';
import BizonFinalScreen from './components/bizon-final-screen/bizon-final-screen';
import DocumentStatus from './components/document-status/document-status';
import { CompanyInfo } from 'generated/credit-analysis-graphql';
import { BIZCONTA_DEEPLINKING, ONBOARDING_DEEPLINKING } from 'env';
import { registerTagmanagerEvent, TagManagerEvent } from 'configs/trackers';
import { getLoanApplicationParams } from 'utils/utils';
import { useGetLoanQuery } from 'generated/graphql';

type DocumentCheckoutProps = {
    companyInfo: CompanyInfo;
    nextScreen(): void;
};

const DocumentCheckout: React.FC<DocumentCheckoutProps> = ({ nextScreen, companyInfo }): JSX.Element => {
    const [deepLinking, setDeeLinking] = useState(ONBOARDING_DEEPLINKING);
    const [email, setEmail] = useState<string>('');
    const loanApplicationId = getLoanApplicationParams();

    const { data, loading  } = useGetLoanQuery({
        variables:{
            id:loanApplicationId
        },
        fetchPolicy:"network-only"
    });

    const checklistLoading = companyInfo === undefined;

    const isCompleted = useMemo(() => {
        return companyInfo && companyInfo.isChecklistUploadCompleted
    }, [companyInfo]);

    const { checklist, signers } = companyInfo;

    const handleRegisterEvent = () => {
        registerTagmanagerEvent(TagManagerEvent.APPROVED);
        nextScreen();
    };

    useEffect(() => {
        if (data?.loanApplication) {
            setEmail(data?.loanApplication.borrower.email);
        }
    }, [data?.loanApplication]);

    useEffect(() => {
        if (companyInfo && companyInfo.id) {
            const linking = `${BIZCONTA_DEEPLINKING}/${companyInfo.id}`;
            setDeeLinking(linking);
        }
    }, [companyInfo]);

     const itemsChecklist = checklist.items.concat(
        signers.reduce((array,signer) => signer.checklist.items,[])
     ).filter(item => item.externalNotification === true);

    return (
        <>
            <div className="bizon-checkout grid-container">
                <BizonSidebar
                    loanApplication={data?.loanApplication}
                    initialClosed={true}
                    className={`bizon-checkout__sidebar ${isCompleted && 'bizon-checkout__sidebar-completed'}`}
                    mainTitle={isCompleted ? undefined : <>falta pouco! não deixe sua <br /> oferta pré-aprovada expirar</>}
                    boxTitle={isCompleted ? <>resumo do seu pedido 🌟</> : <>caso tenha dúvidas, entre em contato pelos canais abaixo:</>}
                    isCompleted={isCompleted}
                />
                <div className={`bizon-checkout__content  ${isCompleted && 'bizon-checkout__content--completed'}`}>
                    {isCompleted
                        ? <BizonFinalScreen email={email} deepLinking={deepLinking} onClick={nextScreen} />
                        : <LoadingFallback
                            loading={loading && checklistLoading}
                            render={() => (
                                <>
                                    <Title className="mt-4 lowercase">
                                       {itemsChecklist.length > 0 ?
                                         'você precisa reenviar documentos' : 'hora de enviar documentos'}
                                    </Title>

                                    <p className="body-1 mt-3">
                                        {itemsChecklist.length > 0 ?
                                         'Envie novamente o documento que está como pendente para finalizarmos a análise de crédito.':
                                         'Envie digitalmente alguns documentos da empresa pra gente terminar a análise de crédito.'
                                        }
                                    </p>
                                    <DocumentStatus companyInfo={companyInfo} />
                                </>
                            )}
                        />
                    }
                </div>
                <div className={['bizon-checkout__footer', isCompleted ? 'bizon-checkout__footer--sm-hide' : null].join(' ')}>
                    {!isCompleted ? (
                        <button
                            type="button"
                            disabled={loading || checklistLoading}
                            className="btn-biz primary"
                            onClick={nextScreen}
                        >
                            vamos começar
                        </button>
                    ) : (
                        <div className="bizon-final-screen__button-wrapper">
                            <a id="bizon-tela-final-baixegratis-lg" href={deepLinking} target='_blank' >
                                <BizosButton onClick={handleRegisterEvent} color="secondary">
                                    Continuar com BizConta
                                </BizosButton>
                            </a>
                        </div>
                    )
                    }
                </div>
                {isCompleted && (
                    <div className="bizon-checkout__bizconta hidden-lg">
                        <a id="bizon-tela-final-baixegratis-sm" href={deepLinking} target='_blank' >
                            <BizosButton onClick={handleRegisterEvent} color="secondary">
                                Continuar com BizConta
                            </BizosButton>
                        </a>
                    </div>
                )}
            </div>
        </>
    );
};

export default DocumentCheckout;
